// Two Firebase apps, both lazy and both optional:
//
// - The DEFAULT app is Studio's own project (aura-dev-36351 / aura-prod-c6045),
//   used for auth ONLY (an ID token for the deployed studio API). It exists
//   only when a web config is present, so the app runs fine against a local
//   backend with STUDIO_AUTH_DISABLED and no Firebase project.
// - The "courses" app is the aura-courses project that holds the course
//   catalog (Firestore + Storage). It exists only when COURSES_ENABLED.

import { getApp, getApps, initializeApp, type FirebaseApp } from "firebase/app";
import { getAuth, type Auth } from "firebase/auth";
import { getFirestore, type Firestore } from "firebase/firestore";
import { getStorage, type FirebaseStorage } from "firebase/storage";
import { COURSES_ENABLED, coursesFirebaseConfig, FIREBASE_ENABLED, firebaseConfig } from "./env";

let app: FirebaseApp | null = null;
let authInstance: Auth | null = null;

export function getFirebaseAuth(): Auth | null {
  if (!FIREBASE_ENABLED) return null;
  if (!authInstance) {
    app = initializeApp(firebaseConfig);
    authInstance = getAuth(app);
  }
  return authInstance;
}

const COURSES_APP_NAME = "courses";

let coursesApp: FirebaseApp | null = null;
let coursesAuth: Auth | null = null;
let coursesDb: Firestore | null = null;
let coursesStorage: FirebaseStorage | null = null;

export function getCoursesApp(): FirebaseApp | null {
  if (!COURSES_ENABLED) return null;
  if (!coursesApp) {
    // Vite HMR can re-run this module after the app was already registered.
    coursesApp = getApps().some((a) => a.name === COURSES_APP_NAME)
      ? getApp(COURSES_APP_NAME)
      : initializeApp(coursesFirebaseConfig, COURSES_APP_NAME);
  }
  return coursesApp;
}

export function getCoursesAuth(): Auth | null {
  const a = getCoursesApp();
  if (!a) return null;
  if (!coursesAuth) coursesAuth = getAuth(a);
  return coursesAuth;
}

export function getCoursesFirestore(): Firestore {
  const a = getCoursesApp();
  if (!a) throw new Error("Courses are not configured for this deployment.");
  if (!coursesDb) coursesDb = getFirestore(a);
  return coursesDb;
}

export function getCoursesStorage(): FirebaseStorage {
  const a = getCoursesApp();
  if (!a) throw new Error("Courses are not configured for this deployment.");
  if (!coursesStorage) coursesStorage = getStorage(a);
  return coursesStorage;
}
